import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { channels } from '@/mocks/channels';
import { articles, getArticle } from '@/mocks/articles';
import { brand, faqs } from '@/mocks/site';
import { ArticleCard } from '@/components/feature/ArticleCard';
import { toneClasses } from '@/components/feature/ChannelPill';
import HotRanking from '@/pages/home/components/HotRanking';
import FocusSection from '@/pages/home/components/FocusSection';

const mainArticle = getArticle(1)!;

const twoChannelSlugs = ['yingshi', 'shehui'];

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  useSeo({
    title: '热瓜速递 - 娱乐八卦、明星资讯、网红爆料、社会热点门户',
    description:
      '热瓜速递是专注娱乐八卦、社会热点、明星资讯、网红爆料与影视综艺的中文资讯门户，提供最新明星动态、网红内幕与热门话题排行，满足不同吃瓜偏好。',
    keywords: '吃瓜网站,娱乐八卦,社会热点,明星资讯,网红爆料,影视综艺',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: brand.name,
        url: '/',
        slogan: brand.slogan,
        description: brand.slogan,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: brand.name,
        inLanguage: 'zh-CN',
        description: '娱乐八卦、社会热点、明星资讯、网红爆料门户',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });

  const latest = articles
    .filter((a) => a.id !== mainArticle.id)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 4);
  const mainChannel = channels.find((c) => c.slug === mainArticle.channel);

  return (
    <div className="pb-4">
      <h1 className="sr-only">热瓜速递 - 娱乐八卦、明星资讯、网红爆料、社会热点资讯门户</h1>

      {/* 栏目直达分类导航 */}
      <section className="mx-auto max-w-6xl px-4 pt-6 md:px-6" aria-labelledby="cat-title">
        <div className="flex items-center justify-between">
          <h2 id="cat-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-compass-3-line text-primary-600" aria-hidden="true" /> 栏目直达
          </h2>
          <span className="text-xs text-foreground-400">按兴趣一键进入，追瓜不迷路</span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {channels.map((c, i) => (
            <Link
              key={c.slug}
              to={`/channel/${c.slug}`}
              className="group relative overflow-hidden rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-200"
            >
              <div className="flex items-start justify-between">
                <span
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-md text-base font-black ${toneClasses(c.accent)}`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 transition-transform duration-300 group-hover:rotate-[-45deg]">
                  <i className="ri-arrow-right-up-line" aria-hidden="true" />
                </span>
              </div>
              <h3 className="mt-4 text-lg font-bold text-foreground-950">{c.name}</h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground-500">{c.tagline}</p>
              <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-400">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 主推 + 热搜榜 */}
      <section className="mx-auto mt-6 grid max-w-6xl gap-5 px-4 md:px-6 lg:grid-cols-5" aria-label="今日主推">
        <Link
          to={`/news/${mainArticle.id}`}
          className="group relative block overflow-hidden rounded-lg lg:col-span-3"
        >
          <div className="relative aspect-[16/10] w-full overflow-hidden md:aspect-[16/9]">
            <img
              src={mainArticle.cover}
              alt={mainArticle.title}
              title={`${mainArticle.title}-热瓜速递`}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/25 to-transparent" />
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
            {mainChannel && (
              <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClasses(mainChannel.accent)}`}>
                <i className="ri-flashlight-fill" aria-hidden="true" /> 今日头条瓜
              </span>
            )}
            <h2 className="mt-3 max-w-2xl text-xl font-bold leading-snug text-background-50 md:text-2xl">
              {mainArticle.title}
            </h2>
            <p className="mt-2 hidden max-w-xl text-sm leading-relaxed text-background-100/85 sm:line-clamp-2">
              {mainArticle.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-background-100/75">
              <span className="inline-flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true" /> {mainArticle.views} 阅读
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true" /> {mainArticle.read}分钟
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-user-line" aria-hidden="true" /> {mainArticle.author}
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 font-medium text-background-50">
                去围观 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </div>
          </div>
        </Link>
        <div className="lg:col-span-2">
          <HotRanking />
        </div>
      </section>

      {/* 编辑精选 */}
      <section className="mx-auto mt-8 max-w-6xl px-4 md:px-6" aria-labelledby="pick-title">
        <div className="mb-4 flex items-center justify-between border-b border-background-200/70 pb-3">
          <h2 id="pick-title" className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
            <i className="ri-sparkling-2-line text-accent-600" aria-hidden="true" /> 本周精选 · 最新上瓜
          </h2>
          <span className="text-xs text-foreground-400">编辑按热度与时效精挑细选</span>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {latest.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      </section>

      {/* 焦点栏目 */}
      <div className="mt-8 space-y-8">
        <FocusSection slug="bagua" />
        <FocusSection slug="mingxing" />
        <FocusSection slug="wanghong" />
      </div>

      {/* 双栏速递（影视综艺 + 社会热点） */}
      <section className="mx-auto mt-8 max-w-6xl px-4 md:px-6" aria-label="更多栏目">
        <div className="grid gap-5 md:grid-cols-2">
          {channels
            .filter((c) => twoChannelSlugs.includes(c.slug))
            .map((c) => {
              const lead = articles.filter((a) => a.channel === c.slug)[0];
              if (!lead) return null;
              return (
                <Link
                  key={c.slug}
                  to={`/channel/${c.slug}`}
                  className="group relative block overflow-hidden rounded-lg"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden">
                    <img
                      src={lead.cover}
                      alt={`${c.name}频道`}
                      title={`${c.name}-热瓜速递`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 to-foreground-950/10" />
                  </div>
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <span className={`inline-flex w-max items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${toneClasses(c.accent)}`}>
                      <i className="ri-more-fill" aria-hidden="true" /> {c.name}
                    </span>
                    <h3 className="mt-2 text-lg font-bold text-background-50">{c.tagline}</h3>
                    <p className="mt-1 line-clamp-1 text-xs text-background-100/75">{c.desc}</p>
                    <span className="mt-2.5 inline-flex items-center gap-1 text-sm font-semibold text-background-50">
                      进入栏目 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              );
            })}
        </div>
      </section>

      {/* 品牌价值 */}
      <section className="mx-auto mt-10 max-w-6xl px-4 md:px-6" aria-labelledby="value-title">
        <div className="rounded-lg bg-secondary-100/60 p-5 md:p-8">
          <div className="grid items-center gap-6 lg:grid-cols-3">
            <div>
              <h2 id="value-title" className="text-xl font-bold text-foreground-950 md:text-2xl">
                为什么选<strong className="text-primary-600">热瓜速递</strong>
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {brand.slogan}。我们坚持真实优先、理性吃瓜，让资讯既有热度也有态度。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                了解我们 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:col-span-2">
              {[
                { icon: 'ri-shield-check-line', t: '真实优先', d: '来源可查、脉络完整' },
                { icon: 'ri-layout-grid-line', t: '多元栏目', d: '五大板块随心逛' },
                { icon: 'ri-eye-line', t: '理性吃瓜', d: '围观有热度亦有分寸' },
                { icon: 'ri-cup-line', t: '有温度', d: '陪伴你的茶余饭后' },
              ].map((v) => (
                <div key={v.t} className="rounded-lg bg-background-50 p-4">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-lg text-accent-700">
                    <i className={v.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-3 text-sm font-bold text-foreground-900">{v.t}</h3>
                  <p className="mt-1 text-xs text-foreground-500">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-8 max-w-6xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-r from-primary-700 via-primary-600 to-primary-700 px-6 py-10 text-center md:px-10">
          <i className="ri-quill-pen-fill absolute -left-4 -top-6 text-[7rem] text-background-50/10" aria-hidden="true" />
          <i className="ri-fire-fill absolute -bottom-6 -right-4 text-[7rem] text-background-50/10" aria-hidden="true" />
          <h2 className="relative text-xl font-bold text-background-50 md:text-2xl">手里有新鲜瓜？快来找我们聊聊</h2>
          <p className="relative mx-auto mt-2 max-w-xl text-sm leading-relaxed text-primary-100">
            无论是圈内线索、现场见闻，还是你想分享的追剧心得，都可一键投稿。
            编辑团队会对每一条认真审核，让好料不埋没。
          </p>
          <Link
            to="/about#contact"
            className="relative mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-400 px-6 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-300"
          >
            <i className="ri-edit-line" aria-hidden="true" /> 我要爆料 / 投稿
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto mt-10 max-w-4xl px-4 md:px-6" aria-labelledby="faq-title">
        <div className="text-center">
          <h2 id="faq-title" className="flex items-center justify-center gap-2 text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-question-answer-line text-primary-600" aria-hidden="true" /> 常见问题
          </h2>
          <p className="mt-2 text-sm text-foreground-500">关于内容来源、投稿与版权，这里一次性说清楚</p>
        </div>
        <div className="mt-6 divide-y divide-background-200/70 rounded-lg border border-background-200/70 bg-background-50">
          {faqs.map((f, i) => {
            const isOpen = openFaq === i;
            return (
              <div key={f.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left md:px-6"
                  aria-expanded={isOpen}
                >
                  <span className="text-[15px] font-semibold text-foreground-900">{f.q}</span>
                  <span
                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-transform duration-300 ${
                      isOpen ? 'rotate-45' : ''
                    }`}
                  >
                    <i className="ri-add-line" aria-hidden="true" />
                  </span>
                </button>
                {isOpen && (
                  <p className="px-4 pb-5 text-sm leading-relaxed text-foreground-600 md:px-6">{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}